import { Chip, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTBContext } from "../../Contexts/useTBStates";
import { placeRegistrations } from "./placeRegistrations";
import { getEffectiveFlights } from "./getEffectiveFlights";
import type { FlightingShape } from "./flightPlacementTypes";

/** Sentinel flight index for drag source when dragging from the unplaced list */
const UNPLACED_FLIGHT_INDEX = -1;

interface FlightingPreviewProps {
    triggerPlacement: boolean;
    populatedFlights: any[];
    setPopulatedFlights: (populatedFlights: any[]) => void;
    /** Up to two registration property names to show in each chip (e.g. 'playerHandicap', 'gender'). */
    chipDisplayFields?: (string | null)[];
}


/** Get display value for a registration field (handicap can be under playerHandicap or handicap). */
function getRegistrationDisplayValue(reg: Record<string, unknown>, fieldName: string): string | undefined {
    if (!fieldName) return undefined;
    const raw = reg[fieldName] ?? (fieldName === 'playerHandicap' ? reg.handicap : undefined);
    if (raw == null || raw === '') return undefined;
    return String(raw);
}

function getChipLabel(reg: Record<string, unknown>, chipDisplayFields?: (string | null)[]): string {
    const name = (reg.playerName ?? 'Unknown') as string;
    const fields = (chipDisplayFields ?? []).filter((f): f is string => Boolean(f));
    const parts = fields.length > 0
        ? fields.map((f) => getRegistrationDisplayValue(reg, f) ?? '-')
        : [getRegistrationDisplayValue(reg, 'playerHandicap') ?? getRegistrationDisplayValue(reg, 'handicap') ?? '-'];
    return `${name} (${parts.join(', ')})`;
}

export const FlightingPreview = ({ triggerPlacement, populatedFlights, setPopulatedFlights, chipDisplayFields }: FlightingPreviewProps) => {

    const { viewEvent, viewEventData } = useTBContext();
    const flighting = viewEvent?.flighting as FlightingShape | undefined;
    const { useDivisionsAsFlights } = getEffectiveFlights(flighting);

    const [registrations, setRegistrations] = useState<any[]>(viewEventData?.registrations || []);
    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        setRegistrations(viewEventData?.registrations || []);
    }, [viewEventData?.registrations]);

    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
        } else {
            const result = placeRegistrations({
                flighting,
                registrations,
            });
            setPopulatedFlights(result);
        }
    }, [triggerPlacement]);

    /** Registrations that are not in any flight (no matching division/flight or placement skipped them) */
    const unplacedRegistrations = useMemo(() => {
        const placedIds = new Set(
            populatedFlights.flatMap((f: any) => (f.players || []).map((p: any) => p._id))
        );
        return registrations.filter((r: any) => r._id && !placedIds.has(r._id));
    }, [registrations, populatedFlights]);

    const handleDragStart = (e: React.DragEvent, playerId: string, fromFlightIndex: number) => {
        e.dataTransfer.setData('playerId', playerId);
        e.dataTransfer.setData('fromFlightIndex', fromFlightIndex.toString());
    };


    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent, toFlightIndex: number) => {
        e.preventDefault();
        const playerId = e.dataTransfer.getData('playerId');
        const fromFlightIndex = parseInt(e.dataTransfer.getData('fromFlightIndex'), 10);

        if (fromFlightIndex === toFlightIndex) return;

        const updatedFlights = JSON.parse(JSON.stringify([...populatedFlights]));
        let playerToMove: any;

        if (fromFlightIndex === UNPLACED_FLIGHT_INDEX) {
            playerToMove = registrations.find((r: any) => r._id === playerId);
        } else {
            playerToMove = updatedFlights[fromFlightIndex]?.players?.find((p: any) => p._id === playerId);
            if (playerToMove) {
                updatedFlights[fromFlightIndex].players = updatedFlights[fromFlightIndex].players.filter((p: any) => p._id !== playerId);
            }
        }

        if (playerToMove) {
            const targetFlight = updatedFlights.find((f: any) => f.index === toFlightIndex);
            if (targetFlight && Array.isArray(targetFlight.players)) {
                targetFlight.players.push(playerToMove);
                setPopulatedFlights(updatedFlights);
            }
        }
    };

    return (

        <Stack direction='column' spacing={2}>
            {flighting?.divisions?.map((division: any, index: number) => (
                <Stack key={index} direction='column' spacing={1}>
                    <Stack id={`division-preview-${index}`}
                        direction='row' spacing={6} alignItems='center' sx={{ borderBottom: '1px solid lightgray', paddingBottom: '5px' }}>
                        <Typography variant='subtitle1'>Division: <span style={{ fontWeight: 600, marginLeft: '10px' }}>{division.divisionName}</span></Typography>
                        {!useDivisionsAsFlights && (
                        <Typography variant='subtitle1'>
                            {populatedFlights.filter((flight: any) => flight.divisionIndex === index).length} flights
                        </Typography>
                        )}
                    </Stack>

                    {populatedFlights
                        .filter((flight: any) => flight.divisionIndex === index)
                        .map((flight: any, flightIndex: number) => (
                            <Stack id={`flight-preview-${index}-${flightIndex}`}
                                direction='row' spacing={2}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, flight.index)}
                                sx={{ 
                                    padding: '10px',
                                    border: '1px dashed #ccc',
                                    borderRadius: '4px',
                                    minHeight: '50px'
                                }}>
                                <Stack key={flightIndex} direction='column' spacing={1} sx={{ width: '100%' }}>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        {!useDivisionsAsFlights && <Typography variant='subtitle2'>{`Flight: ${flight.flightName}`}</Typography>}
                                        {Array.isArray(flight.players) && !useDivisionsAsFlights && <Typography variant='subtitle2'>{`(${flight.players.length})`}</Typography>}
                                    </Stack>
                                    <Stack direction='row' spacing={0} flexWrap="wrap" justifyContent={'flex-start'} alignItems='flex-start'
                                        sx={{ gap: '5px', width: '100%' }} >


                                        {flight.players
                                            .sort((a: any, b: any) => a.playerName.localeCompare(b.playerName))
                                            .map((player: any, playerIndex: number) => (
                                                <Chip
                                                    key={playerIndex}
                                                    label={getChipLabel(player, chipDisplayFields)}
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, player._id, flight.index)}
                                                    sx={{ 
                                                        backgroundColor: 'lightgrey', 
                                                        color: 'black', 
                                                        padding: '0px',
                                                        cursor: 'grab',
                                                        '&:hover': {
                                                            backgroundColor: '#e0e0e0'
                                                        }
                                                    }} 
                                                />
                                            ))}
                                    </Stack>
                                </Stack>
                            </Stack>
                        ))
                    }
                
                </Stack>
            ))}

            {/* Unplaced: registrations that could not be auto-placed; user can drag them into a flight */}
            {unplacedRegistrations.length > 0 && (
                <Stack direction="column" spacing={1} sx={{ marginTop: 2 }}>
                    <Stack
                        id="division-preview-unplaced"
                        direction="row"
                        spacing={6}
                        alignItems="center"
                        sx={{ borderBottom: '1px solid lightgray', paddingBottom: '5px' }}
                    >
                        <Typography variant="subtitle1">
                            Unplaced <span style={{ fontWeight: 600, marginLeft: '10px' }}>({unplacedRegistrations.length})</span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Drag into a division/flight above to assign
                        </Typography>
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={0}
                        flexWrap="wrap"
                        alignItems="flex-start"
                        sx={{
                            gap: '5px',
                            padding: '10px',
                            border: '1px dashed #ccc',
                            borderRadius: '4px',
                            minHeight: '50px',
                            backgroundColor: 'rgba(0,0,0,0.02)',
                        }}
                    >
                        {unplacedRegistrations
                            .sort((a: any, b: any) => (a.playerName || '').localeCompare(b.playerName || ''))
                            .map((reg: any) => (
                                <Chip
                                    key={reg._id}
                                    label={getChipLabel(reg, chipDisplayFields)}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, reg._id, UNPLACED_FLIGHT_INDEX)}
                                    sx={{
                                        backgroundColor: '#fff3e0',
                                        color: 'black',
                                        padding: '0px',
                                        cursor: 'grab',
                                        '&:hover': { backgroundColor: '#ffe0b2' },
                                    }}
                                />
                            ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}
